/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useGetSystemPropertiesQuery, useUpdateSystemMutation } from "../../../store/api/admin/adminApiSlice";
import { setErrorMsg, setOpenToast } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { ISystemProperties } from "../../../store/api/admin/types";
import { xsdPathStr } from "../../../globals/constants";
import { preparPayloadSystemProperties } from "../helpers";
import { systemPropertiesSelector } from "../../../store/api/admin/selectors";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { setSystemProperties } from "../../../store/api/admin/reducers";
import { errorMessage, isEqualObject } from "../../../utils/helpers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { store } from "../../../store";

export const useSysytemOptionsForm = () => {
  const dispatch = useStoreDispatch();
  const storeErrMsg = useSelector(errorMsgSelector);

  const clearInputList = { name: "", location: "", description: "", contact: "" };

  useEffect(() => {
    dispatch(setErrorMsg({ ...storeErrMsg, ...clearInputList }));
  }, []);
  const [formData, setFormData] = useState<ISystemProperties>({
    contact: "",
    description: "",
    location: "",
    name: "",
    maintenanceModeSystem: false,
  });
  const { systemProperties } = xsdPathStr;
  const [updateSystem, { isLoading }] = useUpdateSystemMutation();
  const { data, refetch } = useGetSystemPropertiesQuery();
  const oldSystemProperties = useSelector(systemPropertiesSelector);

  useEffect(() => {
    setFormData({
      name: data?.[systemProperties.name] || "",
      location: data?.[systemProperties.location] || "",
      contact: data?.[systemProperties.contact] || "",
      description: data?.[systemProperties.description] || "",
      maintenanceModeSystem: data?.[systemProperties.maintenanceModeSystem] === "true" || false,
    });
    dispatch(
      setSystemProperties({
        name: data?.[systemProperties.name] || "",
        location: data?.[systemProperties.location] || "",
        contact: data?.[systemProperties.contact] || "",
        description: data?.[systemProperties.description] || "",
        maintenanceModeSystem: data?.[systemProperties.maintenanceModeSystem] === "true" || false,
      })
    );
  }, [data, dispatch, systemProperties]);

  const submitHandler = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Trigger validation logic for empty required fields
    const existing = store.getState().helpers.errorMsg;
    const newErrors = { ...existing };
    let isValid = true;
    
    if (!formData.name?.trim()) {
      newErrors.name = "System Name is required!";
      isValid = false;
    }
    if (!formData.location?.trim()) {
      newErrors.location = "System Location is required!";
      isValid = false;
    }
    
    if (!isValid) {
      dispatch(setErrorMsg(newErrors));
      return false; // Indicate validation failure
    }
    
    if (!isEqualObject(oldSystemProperties, formData)) {
      try {
        const payload = preparPayloadSystemProperties(formData);
        const result: any = await updateSystem(payload);
        if (result?.error) throw result?.error?.message;
        if (result.data?.status === 200) {
          refetch();
          dispatch(setOpenToast({ openToast: true, toastType: "success", message: "System properties update has been initiated." }));
          return true; // Indicate successful submission
        }
      } catch (error: any) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        return false;
      }
    }
    return true; // No changes, but still treat as "successful" for closing dialog/edit mode
  };

  const cancelHandler = () => {
    setFormData({
      name: oldSystemProperties.name || "",
      location: oldSystemProperties.location || "",
      contact: oldSystemProperties.contact || "",
      description: oldSystemProperties.description || "",
      maintenanceModeSystem: oldSystemProperties.maintenanceModeSystem === "true" || false,
    });
    dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, ...clearInputList }));
  };

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const existing = store.getState().helpers.errorMsg;
    if ((name === "name" || name === "location") && !value?.trim()) {
      dispatch(setErrorMsg({ ...existing, [name]: `${name === "name" ? "System Name" : "System Location"} is required!` }));
    }
  };

  return {
    isLoading,
    submitHandler,
    formData,
    setFormData,
    cancelHandler,
    onBlurHandler,
  };
};
